import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { MyTimesheetRoleFilter } from "my-timesheet/components";
import {
  useMyTimesheetPeriodSelector,
  useMyTimesheetWorktimeSummaryWeeklyData,
} from "my-timesheet/hooks";
import { Period } from "my-timesheet/types/period";
import { OcCard, OcDataTable, OcIconForState } from "oc/components";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcRoleFilters,
} from "oc/hooks";
import { RoleFilterInput } from "oc/types";

interface TableData {
  page: number;
  pageSize: number;
  sortField: string;
  sortDirection: "asc" | "desc";
  searchField?: string;
  searchText?: string;
  searched?: boolean;
}

interface RequestData {
  rows: any[];
  total?: number;
  page: number;
  pageSize: number;
  searchField?: string;
  searchText?: string;
}

interface ColumnDef {
  id: string;
  label: string;
  align?: string;
  width?: string | number;
  type?: string;
  sort?: boolean;
  search?: boolean;
}

interface TableDef {
  select: "row" | "none";
  id: string;
  columns: ColumnDef[];
}

interface Props {
  periodStart: string;
  periodEnd: string;
  period: Period;
  product?: string;
  role: string;
  handlePeriodChange: (period: Period) => void;
  isMobile: boolean;
}

export const MyTimesheetWorktimeSummaryWeekly: React.FC<Props> = ({
  periodStart,
  periodEnd,
  period,
  product = "MyTimesheet",
  role,
  handlePeriodChange,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { contractCode: contract } = useOcCurrentUserState();

  const { OcButtonGroup } = useMyTimesheetPeriodSelector({
    period,
    handlePeriodChange,
  });

  const [tableData, setTableData] = useOcTableState(
    "myTimesheetWorkTimeSummaryWeekly",
    {
      page: 1,
      pageSize: 10,
      sortField: "employeeName",
      sortDirection: "asc",
      searchField: "",
      searchText: "",
    },
  );

  const handleTableDataChange = (updatedData: Partial<TableData>) => {
    const keys = Object.keys(updatedData).toString();
    setTableData({ ...tableData, ...updatedData });
    if (
      keys === "searchField" ||
      (keys === "searchText" &&
        updatedData.searchText &&
        updatedData.searchText.length <= 3)
    ) {
      return;
    }
    runQuery();
  };

  useEffect(() => {
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodStart,
    periodEnd,
    period,
    tableData.page,
    tableData.pageSize,
    tableData.sortField,
    tableData.sortDirection,
  ]); // eslint-disable-line

  useEffect(() => {
    if (tableData.searchText && tableData.searchText.length >= 3) {
      runQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.searchText]);

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract,
    product,
    role,
  });

  const {
    columnDefsMobile,
    columnDefsDesktop,
    data,
    loading,
    error,
    runQuery,
    queryData,
  } = useMyTimesheetWorktimeSummaryWeeklyData({
    periodStart,
    periodEnd,
    period,
    tableData,
    roleFilters: roleFilters as RoleFilterInput,
    role,
  });

  const transformedData: TableDef = {
    select: isMobile ? "row" : "none",
    id: isMobile ? columnDefsMobile?.id : columnDefsDesktop?.id,
    columns:
      (isMobile ? columnDefsMobile?.columns : columnDefsDesktop?.columns)?.map(
        (col: any) => ({
          id: col.id,
          label: col.label,
          align: col.align,
          width: col.width,
          type: col.type,
          sort: col.sort,
          search: col.search,
        }),
      ) || [],
  };

  const filteredData = data?.map(({ employeeCode, ...rest }: any) => ({
    ...rest,
    employeeCode,
  }));

  const requestsData: RequestData = {
    rows: filteredData,
    total: queryData?.total,
    page: tableData.page,
    pageSize: tableData.pageSize,
    searchField: tableData.searchField,
    searchText: tableData.searchText,
  };

  useEffect(() => {
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodStart,
    periodEnd,
    period,
    tableData.page,
    tableData.pageSize,
    tableData.sortField,
    tableData.sortDirection,
  ]);

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const onSelectRow = (row: any) => {
    const selectedId = selectedRowId === row?._id ? null : row?._id;
    setSelectedRowId(selectedId);
  };

  const filteredTableDef = {
    id: columnDefsDesktop?.id,
    columns: columnDefsDesktop?.columns?.filter(
      (column: any) =>
        !columnDefsMobile?.columns?.some(
          (mobileColumn: any) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  return (
    <OcCard
      label={t("MY_TIMESHEET_WORKTIME_SUMMARY")}
      loading={loading}
      error={!!error}
    >
      <Box height="16px" />
      <Box display="flex" flexDirection="row" justifyContent="center">
        <OcButtonGroup />
      </Box>
      <Box height="32px" />
      {requestsData.total === 0 ? (
        <OcIconForState
          data={data}
          loading={loading}
          isMobile={isMobile}
          error={!!error}
          module="MyTimesheet"
        />
      ) : (
        <>
          <MyTimesheetRoleFilter
            data={undefined}
            product={product}
            role={role}
            contract={contract}
            resetRoleFilters={resetRoleFilters}
            roleFilters={roleFilters}
            prevRoleFilters={prevRoleFilters}
            setPrevRoleFilters={setPrevRoleFilters}
            handleAddFilter={handleAddFilter}
            handleRemoveFilter={handleRemoveFilter}
            buttonGroup={false}
            runQuery={runQuery}
            refetch={runQuery}
            setOnlyRequested={() => {}}
          />
          <Box height="32px" />
          <OcDataTable
            data={requestsData}
            tableDef={transformedData}
            tableData={tableData}
            setTableData={handleTableDataChange}
            pagination
            onSelectRow={onSelectRow}
            selectedRowId={selectedRowId ?? undefined}
            filteredTableDef={filteredTableDef as any}
          />
        </>
      )}
    </OcCard>
  );
};

export default MyTimesheetWorktimeSummaryWeekly;
